import React, { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { MarkFlags, TextMark, charFlagsToMarks, markHasFlags, markRuns } from './model';

export interface TextRange { from: number; to: number }

/** Inline-Styles einer Formatierung – identisch in Editor und Vorschau. */
export function markCss(flags: MarkFlags): React.CSSProperties {
  const decoration = [flags.underline ? 'underline' : '', flags.strike ? 'line-through' : ''].filter(Boolean).join(' ');
  return {
    fontWeight: flags.bold ? 700 : undefined,
    fontStyle: flags.italic ? 'italic' : undefined,
    textDecoration: decoration || (flags.link ? 'underline' : undefined),
    color: flags.color || (flags.link ? '#2563eb' : undefined),
    backgroundColor: flags.background,
    fontFamily: flags.code ? 'ui-monospace, SFMono-Regular, Menlo, monospace' : flags.fontFamily,
    fontSize: flags.fontSize ? `${flags.fontSize}pt` : undefined,
    letterSpacing: flags.letterSpacing ? `${flags.letterSpacing}px` : undefined,
    fontVariant: flags.smallCaps ? 'small-caps' : undefined,
    verticalAlign: flags.superscript ? 'super' : flags.subscript ? 'sub' : undefined,
    ...(flags.superscript || flags.subscript ? { fontSize: `${(flags.fontSize || 10) * 0.75}pt` } : {}),
  };
}

const BLOCK_TAGS = new Set(['DIV', 'P', 'LI', 'TR', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'BLOCKQUOTE']);

/** Formatierung aus fremdem HTML (Einfügen aus Word, Browser-Auto-Tags). */
export function impliedFlags(element: HTMLElement): MarkFlags {
  const flags: MarkFlags = {};
  const tag = element.nodeName;
  if (tag === 'B' || tag === 'STRONG') flags.bold = true;
  if (tag === 'I' || tag === 'EM') flags.italic = true;
  if (tag === 'U' || tag === 'INS') flags.underline = true;
  if (tag === 'S' || tag === 'STRIKE' || tag === 'DEL') flags.strike = true;
  if (tag === 'SUP') flags.superscript = true;
  if (tag === 'SUB') flags.subscript = true;
  if (tag === 'CODE' || tag === 'KBD' || tag === 'SAMP') flags.code = true;
  if (tag === 'MARK') flags.background = '#fef08a';
  if (tag === 'A') {
    const href = element.getAttribute('href') || '';
    if (/^(https?:|mailto:|tel:)/i.test(href)) flags.link = href;
  }
  const style = element.style;
  if (style) {
    const weight = style.fontWeight;
    if (weight === 'bold' || weight === 'bolder' || Number(weight) >= 600) flags.bold = true;
    if (style.fontStyle === 'italic' || style.fontStyle === 'oblique') flags.italic = true;
    if (style.textDecorationLine?.includes('underline') || style.textDecoration?.includes('underline')) flags.underline = true;
    if (style.textDecorationLine?.includes('line-through') || style.textDecoration?.includes('line-through')) flags.strike = true;
    const color = cssColorToHex(style.color);
    if (color) flags.color = color;
    const background = cssColorToHex(style.backgroundColor);
    if (background && background !== '#ffffff') flags.background = background;
  }
  return flags;
}

function cssColorToHex(value?: string): string | undefined {
  if (!value) return undefined;
  if (/^#[0-9a-f]{6}$/i.test(value)) return value.toLowerCase();
  if (/^#[0-9a-f]{3}$/i.test(value)) return `#${value.slice(1).split('').map(char => char + char).join('')}`.toLowerCase();
  const rgb = value.match(/^rgba?\((\d+)[,\s]+(\d+)[,\s]+(\d+)/i);
  if (!rgb) return undefined;
  return `#${[rgb[1], rgb[2], rgb[3]].map(part => Number(part).toString(16).padStart(2, '0')).join('')}`;
}

/** DOM → Modell: Text samt Formatierungen aus dem contentEditable lesen. */
export function serializeElement(root: HTMLElement): { text: string; marks: TextMark[] } {
  let text = '';
  const chars: MarkFlags[] = [];
  const push = (value: string, flags: MarkFlags) => {
    text += value;
    for (let index = 0; index < value.length; index++) chars.push(flags);
  };
  const walk = (parent: Node, inherited: MarkFlags) => {
    parent.childNodes.forEach(child => {
      if (child.nodeType === 3) { push(child.textContent || '', inherited); return; }
      if (child.nodeType !== 1) return;
      const element = child as HTMLElement;
      if (element.nodeName === 'BR') { push('\n', inherited); return; }
      if (BLOCK_TAGS.has(element.nodeName) && text && !text.endsWith('\n')) push('\n', inherited);
      const encoded = element.dataset?.m;
      let flags = inherited;
      if (encoded) { try { flags = { ...inherited, ...JSON.parse(encoded) }; } catch { flags = { ...inherited, ...impliedFlags(element) }; } }
      else flags = { ...inherited, ...impliedFlags(element) };
      walk(element, flags);
    });
  };
  walk(root, {});
  return { text, marks: charFlagsToMarks(chars) };
}

/** Modell → DOM: ohne innerHTML, damit fremde Inhalte nie als Markup landen. */
export function renderInto(root: HTMLElement, text: string, marks: TextMark[] = []) {
  root.textContent = '';
  for (const run of markRuns(text, marks)) {
    const chunk = text.slice(run.from, run.to);
    if (!markHasFlags(run.flags)) { root.appendChild(document.createTextNode(chunk)); continue; }
    const span = document.createElement('span');
    span.dataset.m = JSON.stringify(run.flags);
    Object.assign(span.style, markCss(run.flags) as any);
    if (run.flags.link) span.title = run.flags.link;
    span.textContent = chunk;
    root.appendChild(span);
  }
}

function containedLength(node: Node): number {
  if (node.nodeType === 3) return (node.textContent || '').length;
  if (node.nodeName === 'BR') return 1;
  return Array.from(node.childNodes).reduce((sum, child) => sum + containedLength(child), 0);
}

const childPrefix = (node: Node, childIndex: number) =>
  Array.from(node.childNodes).slice(0, childIndex).reduce((sum, child) => sum + containedLength(child), 0);

/** Textposition einer DOM-Position – Zeilenumbrüche zählen als ein Zeichen. */
function textOffset(root: HTMLElement, target: Node | null, offset: number): number {
  if (!target) return 0;
  if (target === root) return childPrefix(root, offset);
  let accumulated = 0, found = -1;
  const walk = (parent: Node) => {
    for (const child of Array.from(parent.childNodes)) {
      if (found >= 0) return;
      if (child === target) {
        found = accumulated + (child.nodeType === 3 ? offset : childPrefix(child, offset));
        return;
      }
      if (child.nodeType === 3) accumulated += (child.textContent || '').length;
      else if (child.nodeName === 'BR') accumulated += 1;
      else if (child.nodeType === 1) walk(child);
    }
  };
  walk(root);
  return found >= 0 ? found : accumulated;
}

function locate(root: HTMLElement, offset: number): { node: Node; offset: number } {
  let remaining = Math.max(0, offset);
  let last: { node: Node; offset: number } = { node: root, offset: 0 };
  const walk = (parent: Node): { node: Node; offset: number } | null => {
    for (const child of Array.from(parent.childNodes)) {
      if (child.nodeType === 3) {
        const length = (child.textContent || '').length;
        if (remaining <= length) return { node: child, offset: remaining };
        remaining -= length;
        last = { node: child, offset: length };
      } else if (child.nodeName === 'BR') {
        if (remaining === 0) return { node: parent, offset: Array.from(parent.childNodes).indexOf(child) };
        remaining -= 1;
      } else if (child.nodeType === 1) {
        const found = walk(child);
        if (found) return found;
      }
    }
    return null;
  };
  return walk(root) || last;
}

export function readSelection(root: HTMLElement): TextRange | null {
  const selection = window.getSelection();
  if (!selection || !selection.rangeCount) return null;
  const range = selection.getRangeAt(0);
  if (!root.contains(range.startContainer) || !root.contains(range.endContainer)) return null;
  const from = textOffset(root, range.startContainer, range.startOffset);
  const to = textOffset(root, range.endContainer, range.endOffset);
  return { from: Math.min(from, to), to: Math.max(from, to) };
}

export function writeSelection(root: HTMLElement, range: TextRange) {
  const selection = window.getSelection();
  if (!selection) return;
  const start = locate(root, range.from);
  const end = locate(root, range.to);
  const domRange = document.createRange();
  try {
    domRange.setStart(start.node, start.offset);
    domRange.setEnd(end.node, end.offset);
    selection.removeAllRanges();
    selection.addRange(domRange);
  } catch { /* Bereich nicht mehr vorhanden – Auswahl bleibt unverändert */ }
}

export interface RichTextInputProps {
  text: string;
  marks?: TextMark[];
  ariaLabel: string;
  placeholder?: string;
  className?: string;
  style?: React.CSSProperties;
  /** Erhöhen, um den DOM-Inhalt neu aus dem Modell aufzubauen (nach Formatierung). */
  syncToken?: number;
  caret?: TextRange | null;
  autoFocus?: boolean;
  readOnly?: boolean;
  onChange: (text: string, marks: TextMark[]) => void;
  onSelectionChange?: (range: TextRange | null) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  onSplit?: (range: TextRange) => void;
  onMergeBackward?: () => void;
  onMergeForward?: () => void;
  onIndent?: (outdent: boolean) => boolean;
  onCommand?: (event: React.KeyboardEvent<HTMLDivElement>, range: TextRange) => boolean;
}

/**
 * contentEditable-Feld, das Text und Formatierungen verlustfrei überträgt.
 * Der DOM wird bewusst imperativ befüllt, damit React beim Tippen nie
 * neu abgleicht und die Schreibmarke stehen bleibt.
 */
export default function RichTextInput({
  text, marks = [], ariaLabel, placeholder, className, style, syncToken = 0, caret, autoFocus, readOnly,
  onChange, onSelectionChange, onFocus, onBlur, onSplit, onMergeBackward, onMergeForward, onIndent, onCommand,
}: RichTextInputProps) {
  const root = useRef<HTMLDivElement>(null);
  const composing = useRef(false);
  const lastSync = useRef<number>(-1);
  const latest = useRef({ text, marks });
  latest.current = { text, marks };

  useLayoutEffect(() => {
    const element = root.current;
    if (!element) return;
    const requested = lastSync.current !== syncToken;
    lastSync.current = syncToken;
    const current = serializeElement(element);
    if (requested) {
      renderInto(element, text, marks);
      if (caret) {
        if (document.activeElement !== element) element.focus({ preventScroll: true });
        writeSelection(element, caret);
      }
      return;
    }
    if (document.activeElement === element) return;
    if (current.text !== text || JSON.stringify(current.marks) !== JSON.stringify(marks)) renderInto(element, text, marks);
  }, [syncToken, text, marks, caret]);

  useEffect(() => {
    if (autoFocus && root.current) {
      root.current.focus({ preventScroll: true });
      writeSelection(root.current, caret || { from: text.length, to: text.length });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFocus]);

  const report = useCallback(() => {
    if (!root.current || !onSelectionChange) return;
    onSelectionChange(readSelection(root.current));
  }, [onSelectionChange]);

  const emit = useCallback(() => {
    if (!root.current || composing.current) return;
    const next = serializeElement(root.current);
    onChange(next.text, next.marks);
  }, [onChange]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const element = root.current;
    if (!element) return;
    const range = readSelection(element) || { from: 0, to: 0 };
    if (onCommand?.(event, range)) return;
    if (event.key === 'Enter' && !event.shiftKey && onSplit) {
      event.preventDefault();
      emit();
      onSplit(range);
      return;
    }
    if (event.key === 'Enter' && event.shiftKey) {
      event.preventDefault();
      const selection = window.getSelection();
      if (selection?.rangeCount) {
        const domRange = selection.getRangeAt(0);
        domRange.deleteContents();
        const lineBreak = document.createElement('br');
        domRange.insertNode(lineBreak);
        domRange.setStartAfter(lineBreak);
        domRange.collapse(true);
        selection.removeAllRanges();
        selection.addRange(domRange);
      }
      emit();
      return;
    }
    if (event.key === 'Tab' && onIndent) {
      if (onIndent(event.shiftKey)) { event.preventDefault(); return; }
    }
    if (event.key === 'Backspace' && range.from === 0 && range.to === 0 && onMergeBackward) {
      event.preventDefault();
      onMergeBackward();
      return;
    }
    if (event.key === 'Delete' && range.from === latest.current.text.length && range.to === range.from && onMergeForward) {
      event.preventDefault();
      onMergeForward();
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
    const element = root.current;
    if (!element) return;
    event.preventDefault();
    const html = event.clipboardData.getData('text/html');
    const plain = event.clipboardData.getData('text/plain');
    const holder = document.createElement('div');
    if (html) {
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      parsed.querySelectorAll('script,style,meta,link,head').forEach(item => item.remove());
      const fragment = serializeElement(parsed.body);
      renderInto(holder, fragment.text.replace(/ /g, ' '), fragment.marks);
    } else {
      holder.textContent = plain;
    }
    const selection = window.getSelection();
    if (selection && selection.rangeCount) {
      const range = selection.getRangeAt(0);
      range.deleteContents();
      const fragment = document.createDocumentFragment();
      while (holder.firstChild) fragment.appendChild(holder.firstChild);
      const lastChild = fragment.lastChild;
      range.insertNode(fragment);
      if (lastChild) {
        const after = document.createRange();
        after.setStartAfter(lastChild);
        after.collapse(true);
        selection.removeAllRanges();
        selection.addRange(after);
      }
    }
    emit();
    report();
  };

  return (
    <div
      ref={root}
      role="textbox"
      aria-label={ariaLabel}
      aria-multiline="true"
      data-placeholder={placeholder}
      contentEditable={!readOnly}
      suppressContentEditableWarning
      spellCheck
      className={`rt-input outline-none whitespace-pre-wrap break-words ${className || ''}`}
      style={style}
      onInput={emit}
      onKeyDown={handleKeyDown}
      onKeyUp={report}
      onMouseUp={report}
      onPaste={handlePaste}
      onFocus={() => { onFocus?.(); report(); }}
      onBlur={() => { emit(); onBlur?.(); }}
      onCompositionStart={() => { composing.current = true; }}
      onCompositionEnd={() => { composing.current = false; emit(); }}
    />
  );
}
